import React, { useState } from 'react';
import { Navbar } from './components/common/Navbar';
import { Sidebar, TabType } from './components/common/Sidebar';
import { KpiGrid } from './components/dashboard/KpiGrid';
import { StatusDistributionChart } from './components/dashboard/StatusDistributionChart';
import { DailyCollectionsChart } from './components/dashboard/DailyCollectionsChart';
import { AiSummaryCard } from './components/dashboard/AiSummaryCard';
import { FileUploader } from './components/upload/FileUploader';
import { ColumnMappingWizard } from './components/upload/ColumnMappingWizard';
import { FilterBar } from './components/reconciliation/FilterBar';
import { MasterTable } from './components/reconciliation/MasterTable';
import { BillsBreakdownModal } from './components/reconciliation/BillsBreakdownModal';
import { AiMismatchDrawer } from './components/reconciliation/AiMismatchDrawer';
import { ReportsHub } from './components/reports/ReportsHub';
import { HistoricalRuns } from './components/history/HistoricalRuns';
import { AuditLogsView } from './components/history/AuditLogsView';
import { UserManagement } from './components/admin/UserManagement';
import { MappingRulesConfig } from './components/admin/MappingRulesConfig';
import { useReconciliation } from './context/ReconciliationContext';
import { useAuth } from './context/AuthContext';
import { ReconciliationStatus } from './types/reconciliation';

export const App: React.FC = () => {
  const [currentTab, setCurrentTab] = useState<TabType>('dashboard');
  const [isMappingOpen, setIsMappingOpen] = useState(false);
  const [historySubTab, setHistorySubTab] = useState<'runs' | 'audit'>('runs');
  const [adminSubTab, setAdminSubTab] = useState<'users' | 'rules'>('users');

  const {
    summary,
    items,
    trends,
    isReconciled,
    updateFilters,
    selectedItemForAi,
    setSelectedItemForAi,
    selectedItemForBills,
    setSelectedItemForBills,
  } = useReconciliation();

  const { isAdmin } = useAuth();

  const handleKpiFilter = (status: string) => {
    updateFilters({ statuses: [status as ReconciliationStatus] });
    setCurrentTab('master_table');
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950 transition-colors">
      <Navbar />

      <div className="flex-1 flex flex-col md:flex-row max-w-7xl w-full mx-auto">
        {/* Navigation Sidebar */}
        <Sidebar currentTab={currentTab} onTabChange={setCurrentTab} />

        {/* Main Application Content Body */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto max-w-full">
          {/* TAB 1: EXECUTIVE FINANCIAL DASHBOARD */}
          {currentTab === 'dashboard' && (
            <div className="space-y-6 animate-fade-in">
              {/* Top AI Briefing banner */}
              {summary && items.length > 0 && (
                <AiSummaryCard
                  summary={summary}
                  items={items}
                  onExploreExceptions={() => {
                    updateFilters({
                      statuses: [
                        'Partial Match',
                        'Over Billed',
                        'No Bill Reference',
                        'ERP Only',
                        'Refunded',
                      ],
                    });
                    setCurrentTab('master_table');
                  }}
                />
              )}

              {/* 11 KPI Cards Grid */}
              {summary && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Core Reconciliation Metrics & Ledgers
                    </h2>
                    <span className="text-xs text-slate-400">
                      Click any status card to filter master table
                    </span>
                  </div>
                  <KpiGrid summary={summary} onFilterStatus={handleKpiFilter} />
                </div>
              )}

              {/* Analytics Charts Row */}
              {summary && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <StatusDistributionChart
                    summary={summary}
                    onSelectStatus={handleKpiFilter}
                  />
                  <DailyCollectionsChart trends={trends} />
                </div>
              )}

              {/* Quick Navigation Footer */}
              <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="text-xs text-slate-600 dark:text-slate-400">
                  Ready to audit transactions or export reports for internal and statutory filing?
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentTab('master_table')}
                    className="px-4 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-700 text-white shadow-sm transition"
                  >
                    Open Master Table
                  </button>
                  <button
                    onClick={() => setCurrentTab('reports')}
                    className="px-4 py-2 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition"
                  >
                    Export 8 Reports
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: UPLOAD & COLUMN MAPPING */}
          {currentTab === 'upload' && (
            <div className="animate-fade-in">
              <FileUploader
                onOpenMapping={() => setIsMappingOpen(true)}
                onViewTable={() => setCurrentTab('master_table')}
              />
            </div>
          )}

          {/* TAB 3: MASTER RECONCILIATION TABLE */}
          {currentTab === 'master_table' && (
            <div className="space-y-4 animate-fade-in">
              <FilterBar />
              <MasterTable />
            </div>
          )}

          {/* TAB 4: REPORTS HUB */}
          {currentTab === 'reports' && (
            <div className="animate-fade-in">
              <ReportsHub />
            </div>
          )}

          {/* TAB 5: AUDIT & RUN HISTORY */}
          {currentTab === 'history' && (
            <div className="space-y-6 animate-fade-in">
              {/* Sub-navigation tabs */}
              <div className="flex items-center gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl max-w-fit border border-slate-200 dark:border-slate-700">
                <button
                  onClick={() => setHistorySubTab('runs')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition ${
                    historySubTab === 'runs'
                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  Historical Reconciliations
                </button>
                <button
                  onClick={() => setHistorySubTab('audit')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition ${
                    historySubTab === 'audit'
                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  Immutable Audit Trail
                </button>
              </div>

              {historySubTab === 'runs' ? (
                <HistoricalRuns onSwitchToTable={() => setCurrentTab('master_table')} />
              ) : (
                <AuditLogsView />
              )}
            </div>
          )}

          {/* TAB 6: ADMIN PANEL */}
          {currentTab === 'admin' && isAdmin && (
            <div className="space-y-6 animate-fade-in">
              {/* Admin sub-navigation tabs */}
              <div className="flex items-center gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl max-w-fit border border-slate-200 dark:border-slate-700">
                <button
                  onClick={() => setAdminSubTab('users')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition ${
                    adminSubTab === 'users'
                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  User Management & Roles
                </button>
                <button
                  onClick={() => setAdminSubTab('rules')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition ${
                    adminSubTab === 'rules'
                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  Engine Rules & Tolerances
                </button>
              </div>

              {adminSubTab === 'users' ? <UserManagement /> : <MappingRulesConfig />}
            </div>
          )}
        </main>
      </div>

      {/* GLOBAL MODALS & DRAWERS */}
      {isMappingOpen && (
        <ColumnMappingWizard onClose={() => setIsMappingOpen(false)} />
      )}

      {selectedItemForBills && (
        <BillsBreakdownModal
          item={selectedItemForBills}
          onClose={() => setSelectedItemForBills(null)}
          onOpenAi={(item) => setSelectedItemForAi(item)}
        />
      )}

      {selectedItemForAi && (
        <AiMismatchDrawer
          item={selectedItemForAi}
          onClose={() => setSelectedItemForAi(null)}
          onOpenBills={(item) => setSelectedItemForBills(item)}
        />
      )}
    </div>
  );
};
